import { useEffect, useMemo, useRef, useState } from 'react'
// ProseMirror 官方样式表：提供 .ProseMirror 宿主的 white-space: pre-wrap 等关键规则。
// 缺失时纯空白文档（如单个空格）在 white-space: normal 下不生成行盒、宿主塌缩为
// 0 高度，Chromium 会对 0 高编辑宿主禁用插入命令（queryCommandEnabled('insertText')
// 返回 false），导致该类段落"能打开编辑器但打不了字"。
import 'prosemirror-view/style/prosemirror.css'
import { EditorState, Plugin, TextSelection } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import { Schema, Node } from 'prosemirror-model'
import { keymap } from 'prosemirror-keymap'
import { history, undo, redo } from 'prosemirror-history'
import { baseKeymap } from 'prosemirror-commands'
import { cn } from '@/lib/utils'

// Define the schema
const schema = new Schema({
  nodes: {
    doc: {
      content: 'block+'
    },
    paragraph: {
      content: 'inline*',
      group: 'block',
      parseDOM: [{ tag: 'p' }],
      toDOM() { return ['p', 0] }
    },
    text: {
      group: 'inline'
    },
    formatTag: {
      inline: true,
      group: 'inline',
      attrs: {
        label: { default: '' }
      },
      selectable: true,
      draggable: true,
      atom: true,
      parseDOM: [{
        tag: 'span.format-tag',
        getAttrs: (dom: any) => ({
          label: dom.innerText
        })
      }],
      toDOM(node) {
        return ['span', {
          class: 'format-tag protected',
          contenteditable: 'false',
          style: 'display: inline-flex; vertical-align: baseline;'
        }, node.attrs.label]
      }
    }
  }
})

/**
 * 运行时插入信号
 * @property text - 待插入文本
 * @property nonce - 单调变化的标识，每次插入请求唯一，用于触发 effect
 */
interface InsertSignal {
  text: string
  nonce: number
}

/**
 * 将插入位置规整到段落内部（inline content）。
 * 选区端点/光标落在 block 边界（文档开头/末尾、段落交界）时，直接在该位置插入
 * inline 的 formatTag，ProseMirror 会把 tag 独立包装成新段落，产生"tag 后空行"，
 * 或把 tag 放到错误位置（如结束 tag 落在最后一个标点前、文档末尾多出空段落）。
 * dir=1 用于开始 tag（取后一段开头），dir=-1 用于结束 tag（取前一段末尾）。
 */
const clampToInline = (doc: Node, pos: number, dir: 1 | -1) => {
  const resolved = doc.resolve(pos)
  if (resolved.parent.isTextblock) return pos
  if (dir > 0 && resolved.nodeAfter) return pos + 1
  if (dir < 0 && resolved.nodeBefore) return pos - 1
  return pos
}

/**
 * 光标在 block 边界时的位置规整：优先下一段开头，否则上一段末尾。
 * 用于无选区时的开始/结束 tag 插入（光标可能在文档开头或末尾）。
 */
const clampCursor = (doc: Node, pos: number) => {
  const resolved = doc.resolve(pos)
  if (resolved.parent.isTextblock) return pos
  if (resolved.nodeAfter) return pos + 1
  if (resolved.nodeBefore) return pos - 1
  return pos
}

interface SegmentEditorProps {
  value: string
  sourceText: string
  onChange: (value: string) => void
  onConfirm?: () => void
  onCancel?: () => void
  onBlur?: () => void
  className?: string
  autoFocus?: boolean
  /** mount 时一次性插入的文本（用于"未编辑→进入编辑→插入"场景） */
  initialInsert?: string | null
  /** initialInsert 插入完成后的回调，父组件据此清空 pending 状态 */
  onInitialInsertDone?: () => void
  /** 运行时插入信号（nonce 变化时触发插入到当前光标位置） */
  insertSignal?: InsertSignal | null
  /** insertSignal 消费完成后的回调，父组件据此清空信号避免重复触发 */
  onInsertSignalConsumed?: () => void
}

export function SegmentEditor({
  value,
  sourceText,
  onChange,
  onConfirm,
  onCancel,
  onBlur,
  className,
  autoFocus,
  initialInsert,
  onInitialInsertDone,
  insertSignal,
  onInsertSignalConsumed,
}: SegmentEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)
  const [cmdPressed, setCmdPressed] = useState(false)
  // 待闭合的开始 tag：按 ⌘n 插入 {n} 后进入待闭合态，再按同键插入 {/n}。
  // ref 供 ProseMirror 插件闭包读取最新值（pos 记录开始 tag 插入位置，闭合前验证其仍存在），
  // state 仅驱动 UI 提示。
  const pendingRef = useRef<{ tagId: string; pos: number } | null>(null)
  const [pendingTag, setPendingTag] = useState<string | null>(null)
  // 切键/选区操作被拒时的闪烁计数：作为 key 重建提示元素以重放 CSS 动画
  const [rejectFlash, setRejectFlash] = useState(0)
  // tagIds 仅提取数字标签 {1}、{/1}，用于 ⌘1~⌘9 快捷键插入；
  // ASS 标签 {\an8} 等不参与快捷键（译者无需手动插入，由字幕文件自带）
  const tagIds = useMemo(() => {
    const tags = Array.from(sourceText.matchAll(/\{(\d+)\}/g)).map((m) => m[1])
    return Array.from(new Set(tags))
  }, [sourceText])

  /** 将带格式 tag 的字符串转换为 ProseMirror doc。
   *  支持两类标签：DOCX/PPTX 数字标签 {1}、{/1}，ASS 字幕标签 {\an8}、{\b1} 等。
   *  两类标签都转为 formatTag 原子节点（不可编辑、不可拆分）。
   */
  const stringToDoc = (text: string) => {
    const parts = text.split(/(\{\/?\d+\}|\{\\[^}]+\})/g)
    const inlineContent: any[] = []

    parts.forEach(part => {
      if (part.match(/^\{(?:\/?\d+|\\[^}]+)\}$/)) {
        inlineContent.push(schema.nodes.formatTag.create({ label: part }))
      } else if (part) {
        inlineContent.push(schema.text(part))
      }
    })

    // Ensure we have at least one paragraph with some content or empty
    return schema.nodes.doc.create(null, [
      schema.nodes.paragraph.create(null, inlineContent.length > 0 ? inlineContent : null)
    ])
  }

  /** 将 ProseMirror doc 转回带格式 tag 的字符串。 */
  const docToString = (doc: Node) => {
    let text = ''
    doc.descendants((node) => {
      if (node.isText) {
        text += node.text
      } else if (node.type.name === 'formatTag') {
        text += node.attrs.label
      }
    })
    return text
  }

  useEffect(() => {
    if (!editorRef.current) return

    const state = EditorState.create({
      doc: stringToDoc(value),
      plugins: [
        history(),
        keymap({
          'Mod-z': undo,
          'Mod-y': redo,
          'Mod-Shift-z': redo,
          'Mod-Enter': () => {
            console.debug('[SegmentEditor] 确认段落')
            onConfirm?.()
            return true
          },
          'Escape': () => {
            console.debug('[SegmentEditor] 取消编辑')
            onCancel?.()
            return true
          },
          'Mod-s': () => {
            // Ctrl+S / Cmd+S: 保存并退出编辑
            console.debug('[SegmentEditor] 保存并退出')
            onBlur?.()
            return true
          },
          'Mod-d': () => {
            // Ctrl+D / Cmd+D: 跳过当前段落（取消编辑）
            console.debug('[SegmentEditor] 跳过段落')
            onCancel?.()
            return true
          }
        }),
        keymap(baseKeymap),
        new Plugin({
          props: {
            handleKeyDown(view, event) {
              const isModKey = event.metaKey || event.ctrlKey

              // Esc 优先取消待闭合 tag（不退出编辑），无 pending 时才走 keymap 的 onCancel
              if (event.key === 'Escape' && pendingRef.current !== null) {
                event.preventDefault()
                console.debug('[SegmentEditor] 取消待闭合标签')
                pendingRef.current = null
                setPendingTag(null)
                return true
              }

              if (isModKey && event.key >= '1' && event.key <= '9') {
                event.preventDefault()
                const num = parseInt(event.key)
                if (num <= tagIds.length) {
                  const tagId = tagIds[num - 1]
                  const openTag = `{${tagId}}`
                  const closeTag = `{/${tagId}}`
                  const pending = pendingRef.current

                  const { tr } = view.state
                  const { from, to } = tr.selection

                  // 强制闭合策略：待闭合期间
                  //  1) 按了其他数字键 → 拒绝
                  //  2) 存在文本选区（包裹操作）→ 拒绝，避免产生孤立的开始 tag
                  if (pending !== null) {
                    if (pending.tagId !== tagId || from !== to) {
                      setRejectFlash((n) => n + 1)
                      return true
                    }
                  }

                  const openNode = schema.nodes.formatTag.create({ label: openTag })
                  const closeNode = schema.nodes.formatTag.create({ label: closeTag })

                  if (from !== to) {
                    // 无 pending + 有选区：在选区两侧各插一个 tag，不重排内容。
                    // 不能用 slice + replaceWith：当选区跨段落时，inline tag 会被
                    // ProseMirror 独立包装成空段落，导致"tag 后多一个空行"。
                    // tr.insert 会把 tag 放进相邻 inline 位置，保持段落结构不变。
                    // 但选区端点可能落在 block 边界（全选 from=0 / to=doc.size、
                    // 段落交界），直接在该位置插入会新建独立段落（开始 tag 后空行）
                    // 或把结束 tag 插错位（最后一个标点前/末尾空段落）。
                    // 先用 clampToInline 把端点规整进相邻段落，结束 tag 再经
                    // tr.mapping.map 映射穿过 open 插入带来的位置偏移。
                    const f = clampToInline(tr.doc, from, 1)  // 开始 tag：规整到后一段开头
                    const t = clampToInline(tr.doc, to, -1)   // 结束 tag：规整到前一段末尾
                    tr.insert(f, openNode)
                    tr.insert(tr.mapping.map(t), closeNode)
                    view.dispatch(tr)
                  } else if (pending === null) {
                    // 无选区 + 无 pending：插入开始 tag，进入待闭合态。
                    // 光标若落在 block 边界（文档开头/末尾），用 clampCursor 规整进相邻段落。
                    // pos 记录开始 tag 的实际插入位置，闭合时据此验证它是否仍存在
                    const p = clampCursor(tr.doc, from)
                    tr.insert(p, openNode)
                    view.dispatch(tr)
                    pendingRef.current = { tagId, pos: p }
                    setPendingTag(tagId)
                  } else {
                    // pending.tagId === tagId：插入匹配的结束 tag。
                    // 防御：若开始 tag 已被用户删除（nodeAt 验证失败），本次改为重新插入
                    // 开始 tag 并保持待闭合态，避免产生孤立的结束 tag
                    const nodeAtPos = view.state.doc.nodeAt(pending.pos)
                    const openStillThere = nodeAtPos?.type.name === 'formatTag' && nodeAtPos.attrs.label === openTag
                    if (openStillThere) {
                      const p = clampCursor(tr.doc, from)
                      tr.insert(p, closeNode)
                      view.dispatch(tr)
                      pendingRef.current = null
                      setPendingTag(null)
                    } else {
                      const p = clampCursor(tr.doc, from)
                      tr.insert(p, openNode)
                      view.dispatch(tr)
                      pendingRef.current = { tagId, pos: p }
                    }
                  }
                  return true
                }
              }
              return false
            },
            handleDOMEvents: {
              keydown(_view, event) {
                if (event.metaKey || event.ctrlKey) {
                  setCmdPressed(true)
                }
                return false
              },
              keyup(_view, event) {
                if (event.key === 'Meta' || event.key === 'Control') {
                  setCmdPressed(false)
                }
                return false
              },
              blur() {
                setCmdPressed(false)
                onBlur?.()
                return false
              }
            }
          }
        })
      ]
    })

    const view = new EditorView(editorRef.current, {
      state,
      dispatchTransaction(transaction) {
        const newState = view.state.apply(transaction)
        view.updateState(newState)
        
        if (transaction.docChanged) {
          const newValue = docToString(newState.doc)
          onChange(newValue)
        }
      },
      attributes: {
        class: 'focus:outline-none text-base leading-relaxed'
      }
    })

    viewRef.current = view

    if (autoFocus) {
      setTimeout(() => {
        view.focus()
        // 光标定位到文档末尾（修复初始光标总在最前的问题）
        // 注意：不能用 TextSelection.create(doc, doc.content.size)——该位置解析后
        // parent 是 doc 节点（非 inline content），会抛 RangeError
        // "TextSelection endpoint not pointing into a node with inline content"，
        // 导致光标定位和 initialInsert 插入全部中断。
        // 用 TextSelection.near(..., -1) 从文档末尾向后找最近的有效文本位置
        const end = view.state.doc.content.size
        const selection = TextSelection.near(view.state.doc.resolve(end), -1)
        view.dispatch(view.state.tr.setSelection(selection))
        // 若有 initialInsert（未编辑态触发的插入），聚焦后插入到末尾
        if (initialInsert) {
          const tr = view.state.tr.insertText(initialInsert)
          tr.scrollIntoView()
          view.dispatch(tr)
          onInitialInsertDone?.()
        }
      }, 10)
    }

    return () => {
      view.destroy()
    }
  }, [])

  useEffect(() => {
    if (viewRef.current) {
      const currentValue = docToString(viewRef.current.state.doc)
      if (value !== currentValue) {
        const { state } = viewRef.current
        const tr = state.tr
        tr.replaceWith(0, state.doc.content.size, stringToDoc(value).content)
        viewRef.current.dispatch(tr)
      }
    }
  }, [value])

  /**
   * 运行时插入信号：nonce 变化时把 text 插入到当前光标位置。
   * 若编辑器未聚焦，先 focus 再插入（光标保持上次位置或末尾）。
   * 插入后消费回调，避免重复触发。
   */
  useEffect(() => {
    if (!insertSignal || !viewRef.current) return
    const view = viewRef.current
    view.focus()
    const { state } = view
    const tr = state.tr.insertText(insertSignal.text)
    tr.scrollIntoView()
    view.dispatch(tr)
    onInsertSignalConsumed?.()
  }, [insertSignal?.nonce])

  return (
    <div
      className={cn(
        'relative rounded-md border bg-background text-foreground focus-within:border-ring focus-within:ring-1 focus-within:ring-ring',
        className
      )}
    >
      <div ref={editorRef} className="prosemirror-editor" />
      {tagIds.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-t bg-muted/30 text-[11px] text-muted-foreground">
          <span>标签快捷键:</span>
          {tagIds.map((id, i) => (
            <span key={id} className="inline-flex items-center gap-1">
              <kbd className={cn(
                'px-1 py-0.5 bg-background border rounded text-[10px] font-mono',
                pendingTag === id && 'border-warning text-warning'
              )}>⌘{i + 1}</kbd>
              <span className={cn('font-mono', pendingTag === id ? 'text-warning font-semibold' : 'text-warning')}>{'{'}{id}{'}'}</span>
              {i < tagIds.length - 1 && <span className="text-muted-foreground/50">·</span>}
            </span>
          ))}
          {pendingTag && (
            <span
              key={rejectFlash}
              className={cn('ml-1 font-mono', rejectFlash > 0 && 'animate-pulse')}
            >
              等待插入 {'{/'}{pendingTag}{'}'} · Esc 取消
            </span>
          )}
          <span className="ml-auto text-muted-foreground/70">选中文本后按 ⌘n 一步包裹</span>
        </div>
      )}
      {cmdPressed && (
        <div className="shortcut-hints animate-in fade-in slide-in-from-bottom-1">
          {tagIds.map((id, i) => (
            <div key={id} className={cn('hint-item', pendingTag === id && 'ring-1 ring-warning')}>
              <kbd>⌘{i + 1}</kbd>
              <span className="text-xs text-muted-foreground">{'{'}{id}{'}'}</span>
            </div>
          ))}
          {pendingTag && (
            <div className="hint-item">
              <kbd>Esc</kbd>
              <span className="text-xs text-muted-foreground">取消待闭合标签</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
